import { Link } from 'react-router-dom';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { SEO } from '../components/common/SEO';
import './LegalPages.css';

/**
 * Privacy Policy Page
 * DPO Compliant for Zimbabwe Data Protection Act
 */
export const PrivacyPolicy: React.FC = () => {
  return (
    <div className="legal-page-container">
      <SEO
        title="Privacy Policy | Rongeka Africa"
        description="How Rongeka Africa collects, uses and protects school, student, guardian and staff data, in line with Zimbabwe's data protection law."
        url="https://rongeka.africa/privacy-policy"
      />
      <div className="legal-page-content">
        <Link to="/" className="legal-back-link">
          <ArrowLeftOutlined /> Back to Home
        </Link>

        <h1>Privacy Policy</h1>
        <p className="legal-updated">Last Updated: December 16, 2025</p>
        <p className="legal-compliance">
          <strong>DPO Compliant:</strong> This Privacy Policy complies with the Zimbabwe Data Protection Act 
          and applicable data protection regulations.
        </p>

        <section>
          <h2>1. Introduction</h2>
          <p>
            Rongeka Africa ("we," "our," or "us") is committed to protecting the privacy and security of personal 
            data. This Privacy Policy explains how we collect, use, disclose, and safeguard information when you 
            use our school management platform.
          </p>
        </section>

        <section>
          <h2>2. Data Controller</h2>
          <p>
            Rongeka Africa acts as a data processor for schools using the Platform. Each school is the data 
            controller responsible for the personal data of their students, parents, and staff.
          </p>
        </section>

        <section>
          <h2>3. Information We Collect</h2>
          
          <h3>3.1 School Information</h3>
          <ul>
            <li>School name, code, and address</li>
            <li>Contact email and phone number</li>
            <li>School logo and branding</li>
            <li>Administrator account details</li>
          </ul>

          <h3>3.2 Student Information</h3>
          <ul>
            <li>Full name, date of birth, and gender</li>
            <li>Student registration number</li>
            <li>Class and level information</li>
            <li>Attendance records</li>
            <li>Academic performance and assessment results</li>
            <li>Fee payment records</li>
            <li>Medical information (if provided)</li>
            <li>Guardian/parent contact information</li>
          </ul>

          <h3>3.3 Staff Information</h3>
          <ul>
            <li>Teacher and staff names</li>
            <li>Contact information</li>
            <li>Role and subject assignments</li>
            <li>Employment records</li>
          </ul>

          <h3>3.4 Technical Information</h3>
          <ul>
            <li>IP addresses and device information</li>
            <li>Browser type and version</li>
            <li>Usage data and analytics</li>
            <li>Log files and error reports</li>
          </ul>
        </section>

        <section>
          <h2>4. How We Use Information</h2>
          <p>We use collected information to:</p>
          <ul>
            <li>Provide and maintain the Platform services</li>
            <li>Process student records and attendance</li>
            <li>Generate reports and analytics</li>
            <li>Send notifications and communications to parents</li>
            <li>Process fee payments and financial transactions</li>
            <li>Improve and optimize Platform performance</li>
            <li>Ensure security and prevent fraud</li>
            <li>Comply with legal obligations</li>
          </ul>
        </section>

        <section>
          <h2>5. Legal Basis for Processing (Zimbabwe DPA Compliance)</h2>
          <p>We process personal data based on:</p>
          <ul>
            <li><strong>Consent:</strong> Schools obtain consent from parents/guardians for student data processing</li>
            <li><strong>Contract:</strong> Processing necessary for service delivery to schools</li>
            <li><strong>Legal Obligation:</strong> Compliance with education and data protection laws</li>
            <li><strong>Legitimate Interest:</strong> Service improvement and security measures</li>
          </ul>
        </section>

        <section>
          <h2>6. Data Sharing and Disclosure</h2>
          
          <h3>6.1 We Do Not Sell Data</h3>
          <p>We do not sell, rent, or trade personal data to third parties for marketing purposes.</p>

          <h3>6.2 Service Providers</h3>
          <p>We may share data with trusted service providers who assist in:</p>
          <ul>
            <li>Cloud hosting and storage</li>
            <li>SMS and WhatsApp messaging services</li>
            <li>Payment processing</li>
            <li>Technical support and maintenance</li>
          </ul>

          <h3>6.3 Legal Requirements</h3>
          <p>We may disclose data when required by law or to:</p>
          <ul>
            <li>Comply with legal processes</li>
            <li>Protect rights and safety</li>
            <li>Prevent fraud or security threats</li>
          </ul>
        </section>

        <section>
          <h2>7. Data Security</h2>
          <p>We implement robust security measures including:</p>
          <ul>
            <li>Encryption of data in transit and at rest</li>
            <li>Secure authentication and access controls</li>
            <li>Regular security audits and updates</li>
            <li>Backup and disaster recovery procedures</li>
            <li>Staff training on data protection</li>
          </ul>
        </section>

        <section>
          <h2>8. Data Retention</h2>
          <p>
            8.1. We retain personal data only as long as necessary for the purposes outlined in this policy.<br />
            8.2. Schools control retention periods for student and staff data.<br />
            8.3. Upon account termination, schools have 30 days to export their data.<br />
            8.4. After 30 days, data may be permanently deleted unless legally required to retain.
          </p>
        </section>

        <section>
          <h2>9. Data Subject Rights (Zimbabwe DPA)</h2>
          <p>Under Zimbabwe's Data Protection Act, individuals have the right to:</p>
          <ul>
            <li><strong>Access:</strong> Request copies of their personal data</li>
            <li><strong>Rectification:</strong> Correct inaccurate or incomplete data</li>
            <li><strong>Erasure:</strong> Request deletion of personal data</li>
            <li><strong>Restriction:</strong> Limit processing of personal data</li>
            <li><strong>Portability:</strong> Receive data in a structured, machine-readable format</li>
            <li><strong>Objection:</strong> Object to processing of personal data</li>
            <li><strong>Withdraw Consent:</strong> Withdraw consent at any time</li>
          </ul>
          <p>To exercise these rights, contact your school administrator or email us at support@rongeka.africa</p>
        </section>

        <section>
          <h2>10. Children's Privacy</h2>
          <p>
            Our Platform is designed for schools to manage student data. Schools are responsible for obtaining 
            appropriate parental consent before collecting and processing children's personal data in accordance 
            with Zimbabwe law.
          </p>
        </section>

        <section>
          <h2>11. International Data Transfers</h2>
          <p>
            Data is primarily stored and processed within Zimbabwe or approved Zimbabwean data centers. Any 
            international transfers comply with Zimbabwe Data Protection Act requirements and include 
            appropriate safeguards.
          </p>
        </section>

        <section>
          <h2>12. Cookies and Tracking</h2>
          <p>We use essential cookies to:</p>
          <ul>
            <li>Maintain user sessions</li>
            <li>Remember user preferences</li>
            <li>Ensure security</li>
            <li>Analyze Platform usage (anonymized)</li>
          </ul>
          <p>You can control cookies through your browser settings.</p>
        </section>

        <section>
          <h2>13. Changes to Privacy Policy</h2>
          <p>
            We may update this Privacy Policy periodically. Changes will be posted on this page with an updated 
            "Last Updated" date. Continued use of the Platform after changes constitutes acceptance.
          </p>
        </section>

        <section>
          <h2>14. Data Protection Officer (DPO)</h2>
          <p>
            For data protection inquiries or to exercise your rights, contact our Data Protection Officer:<br />
            Email: support@rongeka.africa
          </p>
        </section>

        <section>
          <h2>15. Complaints</h2>
          <p>
            If you have concerns about how we handle personal data, you may file a complaint with:<br />
            <strong>Zimbabwe Data Protection Authority</strong><br />
            Or contact us directly at support@rongeka.africa
          </p>
        </section>

        <section>
          <h2>16. Contact Us</h2>
          <p>
            For questions about this Privacy Policy or our data practices:<br />
            Email: support@rongeka.africa
          </p>
        </section>

        <div className="legal-footer">
          <Link to="/terms">Terms and Conditions</Link>
          <span>•</span>
          <Link to="/">Back to Home</Link>
        </div>
      </div>
    </div>
  );
};
